/**
 * Question Screen Component
 * Displays question with timer and multi-choice options
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../../common/Card';
import type { Card as CardType, Difficulty } from '../../../types/game';
import type { Question } from '../../../types/question';
import {
  resolveQuestionText,
  resolveQuestionOptions,
  resolveQuestionFeedback,
} from '../../../types/question';
import { getEraSymbol, getLocalizedQuestionEraName } from '../../../utils/historiography';
import { useTranslation } from '../../../hooks/useTranslation';
import styles from './QuestionScreen.module.css';

interface QuestionScreenProps {
  // Current question
  question: Question;
  card: CardType;
  difficulty: Difficulty;
  timeLimit: number;
  timeRemaining: number;

  // Answer state
  selectedOption: number | null;
  hasAnswered: boolean;
  isCorrect: boolean | null;
  xpGain: number;
  eliminatedOptions: number[];
  hintUsed: boolean;

  /** Current streak — used to escalate the fire-streak indicator */
  streak?: number;

  // Actions
  onSelectOption: (index: number) => void;
  onSubmit: () => void;
  onContinue: () => void;
  onHint: () => void;
  onExtendTime: () => void;

  // Labels
  labels: {
    submit: string;
    next: string;
    hint: string;
    extend: string;
    correct: string;
    wrong: string;
    instruction: string;
    timeLimit: (time: number) => string;
    cardLabel: (rank: string, suit: string) => string;
    difficulty: {
      easy: string;
      medium: string;
      hard: string;
    };
    tutorial: {
      title: string;
      line1: string;
      line2Strong: string;
      line2Rest: string;
      dismiss: string;
    };
    hintUsed: string;
    perspectiveLt: string;
    perspectiveBe: string;
    aria: {
      timerRemaining: string;
      timer: string;
      extendTimer: string;
      era: string;
      hintUsed: string;
      hintUse: string;
      submit: string;
      continue: string;
    };
  };
}

const LETTERS = ['A', 'B', 'C', 'D'];
const TUTORIAL_KEY = 'playing-history-seen-tutorial';

export const QuestionScreen: React.FC<QuestionScreenProps> = ({
  question,
  card,
  difficulty,
  timeLimit,
  timeRemaining,
  selectedOption,
  hasAnswered,
  isCorrect,
  xpGain,
  eliminatedOptions,
  hintUsed,
  streak = 0,
  onSelectOption,
  onSubmit,
  onContinue,
  onHint,
  onExtendTime,
  labels,
}) => {
  const { currentLanguage } = useTranslation();
  const lang: 'en' | 'lt' | 'be' =
    currentLanguage === 'lt' || currentLanguage === 'be' ? currentLanguage : 'en';

  const questionText = resolveQuestionText(question, lang);
  const questionOptions = resolveQuestionOptions(question, lang);
  const questionFeedback = resolveQuestionFeedback(question, lang);
  const eraSymbol = getEraSymbol(question.era);
  const eraName = getLocalizedQuestionEraName(question.era, lang);

  // Streak escalation: more flames as the streak grows.
  const streakFire = streak >= 3
    ? '🔥'.repeat(Math.min(streak, 5))
    : '';
  const [showTutorial, setShowTutorial] = useState(
    () => !localStorage.getItem(TUTORIAL_KEY)
  );

  const dismissTutorial = () => {
    localStorage.setItem(TUTORIAL_KEY, '1');
    setShowTutorial(false);
  };

  // Keyboard shortcuts: 1–4 select options, Enter submits/continues
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (showTutorial) return;
      if (['1', '2', '3', '4'].includes(e.key) && !hasAnswered) {
        onSelectOption(parseInt(e.key) - 1);
      } else if (e.key === 'Enter' && !hasAnswered && selectedOption !== null) {
        onSubmit();
      } else if (e.key === 'Enter' && hasAnswered) {
        onContinue();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showTutorial, hasAnswered, selectedOption, onSelectOption, onSubmit, onContinue]);

  // Calculate timer circle
  const circumference = 2 * Math.PI * 18; // r=18
  const offset = circumference - (circumference * (timeRemaining / timeLimit));
  const percentage = timeRemaining / timeLimit;
  const timerColor = percentage > 0.5 ? 'var(--gold)' : percentage > 0.25 ? 'orange' : 'var(--accent)';

  return (
    <section
      id="screen-question"
      className={`screen ${styles.screen} active`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="q-text"
    >
      {showTutorial && (
        <div
          className={styles.tutorialOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="tutorial-heading"
        >
          <div className={styles.tutorialBox}>
            <div className={styles.tutorialIcon} aria-hidden="true">🃏</div>
            <h3 id="tutorial-heading" className={styles.tutorialTitle}>
              {labels.tutorial.title}
            </h3>
            <p className={styles.tutorialText}>{labels.tutorial.line1}</p>
            <p className={styles.tutorialText}>
              <strong>{labels.tutorial.line2Strong}</strong> {labels.tutorial.line2Rest}
            </p>
            <button
              className={styles.tutorialDismiss}
              onClick={dismissTutorial}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            >
              {labels.tutorial.dismiss}
            </button>
          </div>
        </div>
      )}

      <div className={styles.questionModal}>
        {/* Header with card preview */}
        <div className={styles.qHeader}>
          <div className={styles.qCardPreview}>
            <Card card={card} mini />
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                {labels.cardLabel(card.rank, card.suit)}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>
                {labels.instruction}
              </div>
            </div>
          </div>
          <span className={`${styles.diffTag} ${styles[difficulty]}`}>
            {labels.difficulty[difficulty]}
          </span>
        </div>

        {/* Timer */}
        <div className={styles.timerRow} aria-label={labels.aria.timerRemaining}>
          <div className={styles.timerArc} role="timer" aria-label={labels.aria.timer}>
            <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden="true">
              <circle className={styles.arcBg} cx="24" cy="24" r="18" fill="none" strokeWidth="4" />
              <circle
                className={styles.arcFg}
                cx="24"
                cy="24"
                r="18"
                fill="none"
                strokeWidth="4"
                strokeLinecap="round"
                style={{
                  strokeDasharray: circumference,
                  strokeDashoffset: offset,
                  stroke: timerColor,
                }}
              />
            </svg>
            <div className={styles.timerNum}>{timeRemaining}</div>
          </div>
          <div style={{ flex: 1 }}>
            <div className={styles.tierInfo}>
              <span className={`${styles.tierPill} ${styles[difficulty]}`}>
                {labels.difficulty[difficulty]}
              </span>
              <span className={styles.tierTime}>{labels.timeLimit(timeLimit)}</span>
            </div>
          </div>
          {!hasAnswered && (
            <button
              className={styles.timerExtendBtn}
              onClick={onExtendTime}
              aria-label={labels.aria.extendTimer}
            >
              {labels.extend}
            </button>
          )}
        </div>

        {/* Era illustration banner: shows the symbol for the historical
            period associated with this question and the date range. */}
        <div className={styles.eraBanner} aria-label={labels.aria.era}>
          <div className={styles.eraIcon} aria-hidden="true">{eraSymbol.icon}</div>
          <div className={styles.eraTextBlock}>
            <div className={styles.eraName}>{eraName}</div>
            {eraSymbol.dates && (
              <div className={styles.eraDates}>{eraSymbol.dates}</div>
            )}
          </div>
          {streakFire && (
            <div className={styles.streakBadge} aria-label={`Streak ${streak}`}>
              <span aria-hidden="true">{streakFire}</span>
              <span className={styles.streakNum}>×{streak}</span>
            </div>
          )}
        </div>

        {/* Question text */}
        <p className={styles.qText} id="q-text">{questionText}</p>

        {/* Options */}
        <div className={styles.optionsGrid} role="radiogroup" aria-labelledby="q-text">
          {questionOptions.map((option, index) => {
            const isEliminated = eliminatedOptions.includes(index);
            let optionClass = styles.optBtn;
            
            if (hasAnswered) {
              if (index === question.correct) {
                optionClass += ` ${styles.correct}`;
              } else if (index === selectedOption && index !== question.correct) {
                optionClass += ` ${styles.wrong}`;
              }
            } else if (isEliminated) {
              optionClass += ` ${styles.eliminated}`;
            }

            return (
              <button
                key={index}
                className={optionClass}
                onClick={() => onSelectOption(index)}
                disabled={hasAnswered || isEliminated}
                aria-pressed={selectedOption === index}
                role="radio"
                aria-checked={selectedOption === index}
                aria-disabled={isEliminated}
              >
                <span className={styles.optLetter} aria-hidden="true">
                  {LETTERS[index]}
                </span>
                {isEliminated ? '—' : option}
              </button>
            );
          })}
        </div>

        {/* Hint */}
        {!hasAnswered && (
          <div className={styles.hintRow}>
            <button
              className={styles.btnHint}
              onClick={onHint}
              disabled={hintUsed}
              aria-label={hintUsed ? labels.aria.hintUsed : labels.aria.hintUse}
              aria-disabled={hintUsed}
            >
              {hintUsed ? labels.hintUsed : labels.hint}
            </button>
          </div>
        )}

        {/* Feedback panel */}
        {hasAnswered && (
          <div
            className={`${styles.feedbackPanel} ${!isCorrect ? styles.wrong : ''}`}
            role="alert"
            aria-live="assertive"
          >
            <div className={styles.feedbackTitle}>
              {isCorrect ? (
                <>
                  {labels.correct} +{xpGain} XP
                  {streak >= 3 && (
                    <span className={styles.streakInline} aria-hidden="true">
                      {' '}{'🔥'.repeat(Math.min(streak, 5))} streak ×{streak}!
                    </span>
                  )}
                </>
              ) : (
                labels.wrong
              )}
            </div>
            <div className={styles.feedbackBody}>{questionFeedback}</div>
            <div className={styles.feedbackPerspectives}>
              <span className={`${styles.perspTag} ${styles.perspLt}`}>
                {labels.perspectiveLt}: {question.lt_view}
              </span>
              <span className={`${styles.perspTag} ${styles.perspBe}`}>
                {labels.perspectiveBe}: {question.be_view}
              </span>
              <span className={`${styles.perspTag} ${styles.perspNeutral}`}>
                🎓 {question.neutral}
              </span>
            </div>
            <div className={styles.sourceBlock}>
              <span className={styles.sourceLabel} aria-hidden="true">📎 Source</span>
              <span className={styles.sourceText}>{question.source}</span>
            </div>
          </div>
        )}

        {/* Footer buttons */}
        <div className={styles.qFooter}>
          {!hasAnswered ? (
            <button
              className={styles.btnSubmit}
              onClick={onSubmit}
              disabled={selectedOption === null}
              aria-label={labels.aria.submit}
            >
              {labels.submit}
            </button>
          ) : (
            <button
              className={styles.btnNext}
              onClick={onContinue}
              aria-label={labels.aria.continue}
            >
              {labels.next}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default QuestionScreen;
